
import React from 'react';
import { UserRole } from '@/types/database.types';
import { useUpdateUserRole } from '@/hooks/use-profile';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check } from 'lucide-react';
import { toast } from 'sonner';

interface UserRoleManagerProps {
  userId: string;
  currentRole: UserRole;
}

const ROLES: UserRole[] = ['admin', 'finance', 'warehouse', 'seller', 'benchmark', 'benchmark_manager', 'coordinator', 'quality'];
const ROLE_DESCRIPTIONS = {
  admin: 'Full system access',
  finance: 'Financial operations',
  warehouse: 'Inventory management',
  seller: 'Sales operations',
  benchmark: 'Price data entry only',
  benchmark_manager: 'Manage benchmark users & data entry',
  coordinator: 'Farmer & harvest management',
  quality: 'Quality inspection'
};

export function UserRoleManager({ userId, currentRole }: UserRoleManagerProps) {
  const { mutate: updateRole, isPending } = useUpdateUserRole();

  const handleRoleChange = (newRole: UserRole) => {
    updateRole({ userId, role: newRole }, {
      onSuccess: () => {
        toast.success(`Role updated to ${newRole}`);
      },
      onError: (error) => {
        toast.error(`Failed to update role: ${error.message}`);
      }
    });
  };

  return (
    <Select
      value={currentRole}
      onValueChange={(value) => handleRoleChange(value as UserRole)}
      disabled={isPending}
    >
      <SelectTrigger className="w-[180px]">
        <SelectValue>
          <Badge variant={getRoleBadgeVariant(currentRole)}>
            {currentRole}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {ROLES.map((role) => (
          <SelectItem key={role} value={role} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={getRoleBadgeVariant(role)}>
                {role}
              </Badge>
              <span className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</span>
            </div>
            {currentRole === role && <Check className="h-4 w-4" />}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function getRoleBadgeVariant(role: UserRole) {
  switch (role) {
    case 'admin':
      return 'destructive';
    case 'finance':
      return 'success';
    case 'warehouse':
      return 'warning';
    case 'benchmark':
      return 'outline';
    case 'benchmark_manager':
      return 'outline';
    case 'coordinator':
      return 'secondary';
    case 'quality':
      return 'default';
    default:
      return 'secondary';
  }
}
